// analytics.ts
// gatsby-plugin-google-gtag handles script injection and page_view tracking.
// This module manages Consent Mode v2 and custom business events.

const GA_TRACKING_ID = process.env.GATSBY_GA_TRACKING_ID ?? "G-KXLZFNN916";
const ANALYTICS_DEBUG_QUERY_PARAM = "analytics_debug";
const ANALYTICS_DEBUG_STORAGE_KEY = "ccs_analytics_debug";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

type AnalyticsPrimitive = string | number | boolean;
type AnalyticsParams = Record<string, AnalyticsPrimitive | null | undefined>;

export type ConversionType =
  | "audit_request"
  | "quote_request"
  | "maintenance_request"
  | "strategic_review_request"
  | "similar_project_request"
  | "contact_request"
  | "whatsapp_click"
  | "portfolio_click"
  | "service_cta_click";

// ── Debug mode ───────────────────────────────────────────────────────────────
// Activate with ?analytics_debug=1 — persists to localStorage across pages.

export const isGADebugMode = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    if (new URLSearchParams(window.location.search).get(ANALYTICS_DEBUG_QUERY_PARAM) === "1") {
      window.localStorage.setItem(ANALYTICS_DEBUG_STORAGE_KEY, "true");
      return true;
    }
    return window.localStorage.getItem(ANALYTICS_DEBUG_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

export const resolveAnalyticsDebugPreference = ({
  search,
  storageValue,
}: {
  search?: string;
  storageValue?: string | null;
}) => {
  if (new URLSearchParams(search || "").get(ANALYTICS_DEBUG_QUERY_PARAM) === "1") return true;
  return storageValue === "true";
};

export const shouldLogAnalyticsDebug = () => isGADebugMode();

const log = (label: string, payload?: Record<string, unknown>) => {
  if (!isGADebugMode()) return;
  console.info(`[ccs analytics] ${label}`, payload ?? {});
};

// ── Consent Mode v2 ──────────────────────────────────────────────────────────
// gtag is already available — injected by gatsby-plugin-google-gtag.

export const grantAnalyticsConsent = () => {
  if (typeof window === "undefined") return;
  window.gtag?.("consent", "update", {
    analytics_storage: "granted",
    ...(isGADebugMode() ? { debug_mode: true } : {}),
  });
  // Re-send config so GA picks up the consent grant and debug flag.
  window.gtag?.("config", GA_TRACKING_ID, {
    anonymize_ip: true,
    ...(isGADebugMode() ? { debug_mode: true } : {}),
  });
  // Fire first page_view now that consent is granted.
  window.gtag?.("event", "page_view", {
    page_path: typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : "/",
    page_title: typeof document !== "undefined" ? document.title : undefined,
    page_language: typeof document !== "undefined"
      ? document.documentElement.lang || undefined
      : undefined,
    ...(isGADebugMode() ? { debug_mode: true } : {}),
  });
  log("consent granted + page_view fired", { trackingId: GA_TRACKING_ID });
};

export const revokeAnalyticsConsent = () => {
  if (typeof window === "undefined") return;
  window.gtag?.("consent", "update", { analytics_storage: "denied" });
  // Clear GA cookies so no data persists.
  document.cookie.split("; ")
    .map(c => c.split("=")[0])
    .filter(n => n === "_ga" || n.startsWith("_ga_"))
    .forEach(n => {
      document.cookie = `${n}=; Path=/; Max-Age=0; SameSite=Lax`;
    });
  log("consent revoked");
};

// ── initializeAnalytics / disableAnalytics ───────────────────────────────────
// Kept for backward-compat with gatsby-browser.js and CookieBanner.

export const initializeAnalytics = async () => grantAnalyticsConsent();
export const disableAnalytics = () => revokeAnalyticsConsent();

// ── Event helpers ────────────────────────────────────────────────────────────

const baseParams = (): AnalyticsParams => ({
  page_path: typeof window !== "undefined"
    ? `${window.location.pathname}${window.location.search}${window.location.hash}`
    : "/",
  page_title: typeof document !== "undefined" ? document.title : undefined,
  page_language: typeof document !== "undefined"
    ? document.documentElement.lang || undefined
    : undefined,
  ...(isGADebugMode() ? { debug_mode: true } : {}),
});

export const trackPageView = async (pagePath?: string) => {
  if (typeof window === "undefined") return;
  const path = pagePath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`;
  log("page_view", { page_path: path });
  window.gtag?.("event", "page_view", {
    page_path: path,
    page_title: document.title,
    page_language: document.documentElement.lang || undefined,
    ...(isGADebugMode() ? { debug_mode: true } : {}),
  });
};

export const trackEvent = async (
  eventName: string,
  params: AnalyticsParams = {}
) => {
  if (typeof window === "undefined") return;
  const payload = { ...baseParams(), ...params };
  log(eventName, payload as Record<string, unknown>);
  window.gtag?.("event", eventName, payload);
};

// ── CTA & lead tracking ──────────────────────────────────────────────────────

type CtaClickParams = {
  cta_name: string;
  cta_location: string;
  destination?: string;
  destination_type?: "internal" | "external" | "mailto" | "phone" | "whatsapp";
};

const CONTACT_INTENT_TO_CONVERSION: Record<string, ConversionType> = {
  audit: "audit_request",
  quote: "quote_request",
  maintenance: "maintenance_request",
  "strategic-review": "strategic_review_request",
  "similar-project": "similar_project_request",
};

const LEAD_TYPE_TO_CONVERSION: Record<string, ConversionType> = {
  audit: "audit_request",
  audit_request: "audit_request",
  quote: "quote_request",
  maintenance: "maintenance_request",
  strategicReview: "strategic_review_request",
  strategic_review: "strategic_review_request",
  similarProject: "similar_project_request",
  similar_project: "similar_project_request",
  contact_request: "contact_request",
};

const getContactIntent = (destination?: string) => {
  if (!destination?.includes("/contact/")) return undefined;
  const query = destination.split("?")[1]?.split("#")[0];
  return query ? new URLSearchParams(query).get("intent") ?? undefined : undefined;
};

export const resolveConversionTypeFromCta = ({
  destination,
  destination_type = "internal",
}: Pick<CtaClickParams, "destination" | "destination_type">): ConversionType | undefined => {
  if (destination_type === "whatsapp") return "whatsapp_click";
  const intent = getContactIntent(destination);
  if (intent && CONTACT_INTENT_TO_CONVERSION[intent]) return CONTACT_INTENT_TO_CONVERSION[intent];
  if (destination?.includes("/portfolio/")) return "portfolio_click";
  if (destination?.includes("/services/")) return "service_cta_click";
  return undefined;
};

export const resolveLeadConversionType = (lead_type: string) =>
  LEAD_TYPE_TO_CONVERSION[lead_type];

export const trackCtaClick = async ({ cta_name, cta_location, destination, destination_type = "internal" }: CtaClickParams) => {
  const conversion_type = resolveConversionTypeFromCta({ destination, destination_type });
  await trackEvent("select_content", { content_type: "cta", cta_name, cta_location, destination, destination_type, conversion_type });
  if (conversion_type) {
    await trackEvent("ccs_conversion", { conversion_type, source_event: "select_content", cta_name, cta_location, destination, destination_type });
  }
};

export const trackLeadCapture = async ({ form_name, lead_type, method }: { form_name: string; lead_type: string; method?: string }) => {
  const conversion_type = LEAD_TYPE_TO_CONVERSION[lead_type] ?? "contact_request";
  await trackEvent("generate_lead", { form_name, lead_type, method, conversion_type });
  await trackEvent("ccs_conversion", { conversion_type, source_event: "generate_lead", form_name, lead_type, method });
};
